import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDueDiligenceCategory,
  useDueDiligenceResponses,
  useUpsertBulkDueDiligenceResponses,
} from 'hooks/useDueDiligence';
import { useUsers } from 'shared/contexts/users.context';
import { DueDiligenceAnswerType } from 'shared/types/due-diligence';
import DueDiligenceDetailHeader from '../components/dueDiligenceDetail/DueDiligenceDetailHeader';
import DueDiligenceCumulativeSection from '../components/dueDiligenceDetail/DueDiligenceCumulativeSection';
import DueDiligenceQuestionList from '../components/dueDiligenceDetail/DueDiligenceQuestionList';

interface ResponseState {
  questionId: number;
  selectedOptionId: number | null;
  notes: string;
  freeTextAnswer?: string | null;
  numberAnswer?: number | null;
  dateAnswer?: string | null;
  booleanAnswer?: boolean | null;
}

/**
 * Detail page for viewing and editing Due Diligence responses for a category.
 * Opens in a new tab from the DD overview cards.
 */
const DueDiligenceCategoryDetailPage: React.FC = () => {
  const { buildingId, unitId, categoryId } = useParams<{
    buildingId: string;
    unitId?: string;
    categoryId: string;
  }>();
  const navigate = useNavigate();

  // Hooks for data fetching
  const { category, isLoading: isLoadingCategory } = useDueDiligenceCategory(
    categoryId ? parseInt(categoryId) : null
  );
  const { responses, isLoading: isLoadingResponses, refetch: refetchResponses } = useDueDiligenceResponses(
    buildingId,
    unitId,
    categoryId ? parseInt(categoryId) : undefined
  );
  const { upsertBulkResponses, isUpserting } = useUpsertBulkDueDiligenceResponses();
  const { users } = useUsers();

  // Local state
  const [responseStates, setResponseStates] = useState<ResponseState[]>([]);
  const [initialStates, setInitialStates] = useState<ResponseState[]>([]);

  // Get cumulative labels from questions
  const cumulativeLabels = useMemo(() => {
    if (!category?.questions) return [];
    const labels: Array<{ labelId: number; labelName: string; questionId: number }> = [];
    const seenLabelIds = new Set<number>();

    category.questions
      .filter((q) => q.active)
      .forEach((question) => {
        question.documentLabels?.forEach((label) => {
          // Check both old FileLabel behavior and new Question behavior
          const isCumulative = question.documentBehavior === 'cumulative' || label.documentBehavior === 'cumulative';
          if (isCumulative && !seenLabelIds.has(label.id)) {
            seenLabelIds.add(label.id);
            labels.push({
              labelId: label.id,
              labelName: label.label || label.name,
              questionId: question.id,
            });
          }
        });
      });

    return labels;
  }, [category?.questions]);

  // Helper to get user name from clerk userId
  const getUserName = useCallback((userId: string | undefined) => {
    if (!userId) return null;
    const user = users.find((u) => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : userId;
  }, [users]);

  // Initialize response states when data loads
  useEffect(() => {
    if (category?.questions) {
      const initialStatesData: ResponseState[] = category.questions
        .filter(q => q.active)
        .map((question) => {
          const existingResponse = responses.find(r => r.questionId === question.id);
          const defaultOption = question.answerOptions?.find(opt => opt.isDefault && opt.active);

          // Try to determine selectedOptionId
          let selectedOptionId = existingResponse?.selectedOptionId ?? null;

          // If response exists but selectedOptionId is null, try to match based on sourceFileLog
          if (existingResponse && !selectedOptionId && existingResponse.sourceFileLog) {
            const positiveOption = question.answerOptions?.find(opt =>
              opt.active && (
                opt.statusColor === 'green' ||
                opt.value?.toLowerCase() === 'aanwezig' ||
                opt.value?.toLowerCase() === 'ja' ||
                opt.label?.toLowerCase().includes('aanwezig') ||
                opt.label?.toLowerCase() === 'ja'
              )
            );
            if (positiveOption) {
              selectedOptionId = positiveOption.id;
            }
          }

          // Fall back to default option if still no selection
          if (!selectedOptionId) {
            selectedOptionId = defaultOption?.id ?? null;
          }

          return {
            questionId: question.id,
            selectedOptionId,
            notes: existingResponse?.notes ?? '',
            freeTextAnswer: existingResponse?.freeTextAnswer ?? null,
            numberAnswer: existingResponse?.numberAnswer ?? null,
            dateAnswer: existingResponse?.dateAnswer ?? null,
            booleanAnswer: existingResponse?.booleanAnswer ?? null,
          };
        });

      setResponseStates(initialStatesData);
      setInitialStates(JSON.parse(JSON.stringify(initialStatesData)));
    }
  }, [category, responses]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (responseStates.length !== initialStates.length) return true;
    return responseStates.some((state, idx) => {
      const initial = initialStates[idx];
      if (!initial) return true;
      return (
        state.selectedOptionId !== initial.selectedOptionId ||
        state.notes !== initial.notes ||
        state.freeTextAnswer !== initial.freeTextAnswer ||
        state.numberAnswer !== initial.numberAnswer ||
        state.dateAnswer !== initial.dateAnswer ||
        state.booleanAnswer !== initial.booleanAnswer
      );
    });
  }, [responseStates, initialStates]);

  // Warn on navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Update response state
  const updateResponseState = useCallback((questionId: number, updates: Partial<ResponseState>) => {
    setResponseStates(prev =>
      prev.map(state =>
        state.questionId === questionId ? { ...state, ...updates } : state
      )
    );
  }, []);

  // Handle save
  const handleSave = async () => {
    try {
      // Filter responses that have either an option selected OR an extracted value
      const validResponses = responseStates.filter(r => {
        const question = category?.questions?.find(q => q.id === r.questionId);
        if (question?.answerType === DueDiligenceAnswerType.EXTRACTION) {
          return r.freeTextAnswer !== null || r.numberAnswer !== null ||
                 r.dateAnswer !== null || r.booleanAnswer !== null;
        }
        return r.selectedOptionId !== null;
      });

      if (validResponses.length === 0) {
        toast.info('Geen antwoorden om op te slaan');
        return;
      }

      await upsertBulkResponses({
        responses: validResponses.map(r => {
          const question = category?.questions?.find(q => q.id === r.questionId);
          return {
            questionId: r.questionId,
            selectedOptionId: r.selectedOptionId ?? undefined,
            notes: r.notes || undefined,
            buildingId,
            unitId,
            ...(question?.answerType === DueDiligenceAnswerType.EXTRACTION && {
              freeTextAnswer: r.freeTextAnswer ?? undefined,
              numberAnswer: r.numberAnswer ?? undefined,
              dateAnswer: r.dateAnswer ?? undefined,
              booleanAnswer: r.booleanAnswer ?? undefined,
            }),
          };
        }),
      });

      toast.success('Due diligence antwoorden opgeslagen');
      refetchResponses();
      // Update initial states to match current (no more "unsaved" indicator)
      setInitialStates(JSON.parse(JSON.stringify(responseStates)));
    } catch (error) {
      console.error('Error saving due diligence responses:', error);
      toast.error('Kon antwoorden niet opslaan');
    }
  };

  const isLoading = isLoadingCategory || isLoadingResponses;

  // Validate params
  if (!categoryId || !buildingId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600">Ongeldige parameters</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:underline"
          >
            Terug naar vorige pagina
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 shadow-sm p-4">
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <DueDiligenceDetailHeader
        buildingId={buildingId}
        unitId={unitId}
        categoryName={category?.name}
        onSave={handleSave}
        isSaving={isUpserting}
        hasChanges={hasUnsavedChanges}
      />

      {/* Content */}
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Cumulative Timeline Section */}
        {cumulativeLabels.length > 0 && (
          <DueDiligenceCumulativeSection
            cumulativeLabels={cumulativeLabels}
            buildingId={buildingId}
            unitId={unitId}
          />
        )}

        {/* Questions List */}
        <DueDiligenceQuestionList
          questions={category?.questions ?? []}
          responses={responses}
          responseStates={responseStates}
          onResponseChange={updateResponseState}
          getUserName={getUserName}
          buildingId={buildingId}
          unitId={unitId}
        />
      </div>
    </div>
  );
};

export default DueDiligenceCategoryDetailPage;
